import React, { useRef, useEffect, useMemo } from 'react';
import './VerticalRangeCalendar.css';

interface VerticalRangeCalendarProps {
    startDate: string; // YYYY-MM-DD
    endDate: string;   // YYYY-MM-DD
    onChange: (start: string, end: string) => void;
}

const VerticalRangeCalendar: React.FC<VerticalRangeCalendarProps> = ({ startDate, endDate, onChange }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const didInitialScroll = useRef(false);


    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    const months = useMemo(() => {
        const result = [];
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        
        // If we have a startDate, and it's before today, start our 12 months from then
        if (startDate) {
            const selectedDate = new Date(startDate);
            if (selectedDate < start) {
                start.setFullYear(selectedDate.getFullYear());
                start.setMonth(selectedDate.getMonth());
            }
        }

        for (let i = 0; i < 24; i++) { // Show 24 months to be safe
            const date = new Date(start.getFullYear(), start.getMonth() + i, 1);
            result.push({
                year: date.getFullYear(),
                month: date.getMonth(),
                id: `${date.getFullYear()}-${date.getMonth()}`
            });
        }
        return result;
    }, []); // Only generate month list once on mount


    useEffect(() => {
        // Only perform auto-scroll on initial mount if a start date is already provided
        if (startDate && scrollContainerRef.current) {
            const selectedDate = new Date(startDate);
            const targetId = `${selectedDate.getFullYear()}-${selectedDate.getMonth()}`;
            
            const timer = setTimeout(() => {
                const targetElement = scrollContainerRef.current?.querySelector(`[data-month-id="${targetId}"]`);
                if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'auto', block: 'start' });
                }
            }, 50);
            return () => clearTimeout(timer);
        }
    }, []); // Empty dependency array means this only runs once when the calendar is opened



    const handleDateClick = (year: number, month: number, day: number) => {
        const clickedDate = new Date(year, month, day);
        // Normalize to local date string YYYY-MM-DD to avoid timezone issues
        const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

        if (!startDate || (startDate && endDate)) {
            // Start fresh
            onChange(iso, '');
        } else {
            // Setting the end
            if (iso < startDate) {
                // If user selects a date before start, make it the new start
                onChange(iso, '');
            } else if (iso === startDate) {
                // Toggle off
                onChange('', '');
            } else {
                onChange(startDate, iso);
            }
        }
    };

    const isSelected = (year: number, month: number, day: number) => {
        const d = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return d === startDate || d === endDate;
    };

    const isInRange = (year: number, month: number, day: number) => {
        if (!startDate || !endDate) return false;
        const d = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return d > startDate && d < endDate;
    };

    const isToday = (year: number, month: number, day: number) => {
        const today = new Date();
        return (
            day === today.getDate() &&
            month === today.getMonth() &&
            year === today.getFullYear()
        );
    };

    const isPast = (year: number, month: number, day: number) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const date = new Date(year, month, day);
        return date < today;
    };

    const renderMonth = (year: number, month: number) => {
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];

        // Empty slots
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="v-calendar-day empty"></div>);
        }

        // Days
        for (let d = 1; d <= daysInMonth; d++) {
            const selected = isSelected(year, month, d);
            const inRange = isInRange(year, month, d);
            const start = startDate === `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const end = endDate === `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const past = isPast(year, month, d);

            days.push(
                <div 
                    key={d} 
                    className={`v-calendar-day-wrapper ${inRange ? 'in-range' : ''} ${start ? 'range-start' : ''} ${end ? 'range-end' : ''}`}
                >
                    <button
                        type="button"
                        className={`v-calendar-day ${selected ? 'selected' : ''} ${isToday(year, month, d) ? 'today' : ''} ${past ? 'past' : ''}`}
                        onClick={() => !past && handleDateClick(year, month, d)}
                        disabled={past}
                    >
                        {d}
                    </button>
                </div>
            );
        }

        return (
            <div key={`${year}-${month}`} className="v-calendar-month" data-month-id={`${year}-${month}`}>
                <h3 className="v-calendar-month-name">{monthNames[month]} {year}</h3>
                <div className="v-calendar-weekdays">
                    {weekdays.map(wd => <div key={wd} className="v-calendar-weekday">{wd}</div>)}
                </div>
                <div className="v-calendar-grid">
                    {days}
                </div>
            </div>
        );
    };

    return (
        <div className="vertical-range-calendar-container" ref={scrollContainerRef}>
            <div className="v-calendar-scroll-area">
                {months.map(m => renderMonth(m.year, m.month))}
            </div>
        </div>
    );
};

export default VerticalRangeCalendar;
